// Panel de resultado al vender una orden de Farmacia (PR 8.3) — cierra "vendida y facturada" falso: la
// bandeja de salida de facturación es ASÍNCRONA (PLAN.md §6.1), así que el HTTP 200 de "vender" solo
// confirma que la orden/pago/factura se registraron localmente, nunca que el SRI ya autorizó nada. Mismo
// patrón que ya prueba `packages/web/src/web/pages/admin/cobrar-resultado.tsx` (comprobante HTML
// provisional en un iframe mientras la factura no tiene número, sondeo automático a los 15/45/120 s +
// botón manual, PDF real una vez autorizada), reescrito acá porque `hce-chart` es un paquete de frontend
// separado (sin Mantine/@medplum/react) que no puede importar componentes de `packages/web`.
import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, Clock, FlaskConical, Loader2, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { actualizarEntregaFacturaFarmacia, descargarComprobanteFarmacia, type FacturaResumenFarmacia, type ToneEstadoFacturaFarmacia } from '@/lib/farmaciaInterna';
import { descargar } from './ComprobantesFarmacia';
import { SONDEO_ENTREGA_MS, debeSondearEntrega } from './venta-resultado-logica';

export type ResultadoVenta = {
  /** Identifica ESTA venta (no la orden): distingue una venta nueva de la anterior para reiniciar el sondeo. */
  key: number;
  orderId: number;
  totalCents: number;
  printableHtml: string;
  factura: FacturaResumenFarmacia;
  vueltoTexto: string;
  avisoDispensacion?: string;
};

const TONO_A_VARIANTE: Record<ToneEstadoFacturaFarmacia, 'default' | 'orange' | 'red'> = { verde: 'default', ambar: 'orange', rojo: 'red', gris: 'default' };

type Props = { resultado: ResultadoVenta; onCerrar: () => void };

export function ResultadoVentaFarmacia({ resultado, onCerrar }: Props): JSX.Element {
  const { t } = useTranslation();
  // La factura tal como la dejó la última consulta al SRI; hasta entonces, la que trajo "vender".
  const [facturaViva, setFacturaViva] = useState<FacturaResumenFarmacia | null>(null);
  const [consultando, setConsultando] = useState(false);
  const [errorActualizar, setErrorActualizar] = useState(false);
  const factura = facturaViva ?? resultado.factura;
  const estado = factura.estado;
  const sondear = debeSondearEntrega(factura);
  // Lo que el temporizador consulta al dispararse (siempre lo último, sin rearmar los temporizadores en cada render).
  const vivo = useRef({ sondear, consultar: async () => {} });
  // La venta que muestra el panel AHORA (mismo patrón que `idActual` de cobrar-resultado.tsx): una respuesta que
  // llega tarde de una venta anterior (se confirmó otra orden mientras esta consulta viajaba) no se aplica.
  const ventaActual = useRef(resultado.key);
  ventaActual.current = resultado.key;

  async function consultar(): Promise<void> {
    if (factura.invoiceId == null) return;
    const key = resultado.key;
    setConsultando(true);
    setErrorActualizar(false);
    try {
      const r = await actualizarEntregaFacturaFarmacia(factura.invoiceId);
      if (ventaActual.current !== key) return;
      if (r.ok) setFacturaViva(r.datos.factura);
      else setErrorActualizar(true);
    } catch {
      if (ventaActual.current === key) setErrorActualizar(true);
    } finally {
      if (ventaActual.current === key) setConsultando(false);
    }
  }
  vivo.current = { sondear, consultar };

  useEffect(() => {
    // Venta nueva (otra orden, u otra venta de la misma orden): lo consultado de la anterior no vale.
    setFacturaViva(null);
    setErrorActualizar(false);
    if (!debeSondearEntrega(resultado.factura)) return;
    const temporizadores = SONDEO_ENTREGA_MS.map((ms) =>
      setTimeout(() => {
        // Ya autorizada o rechazada (por una consulta anterior o por el botón): no se pregunta más.
        if (vivo.current.sondear) void vivo.current.consultar();
      }, ms),
    );
    return () => temporizadores.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al aparecer ESTA venta: los temporizadores leen el estado vivo por `vivo`.
  }, [resultado.key]);

  async function bajarComprobante(): Promise<void> {
    if (factura.invoiceId == null) return;
    const r = await descargarComprobanteFarmacia(factura.invoiceId, 'ride');
    if (r.ok) descargar(r.datos.filename, r.datos.contentType, r.datos.content);
  }

  const Icono = estado?.estado === 'rechazada' ? AlertTriangle : estado?.estado === 'autorizada' ? CheckCircle2 : estado?.estado === 'de_prueba' ? FlaskConical : Clock;
  const variante = estado ? TONO_A_VARIANTE[estado.tono] : 'orange';
  const tieneNumero = factura.numero != null;

  return (
    <div className="space-y-3 rounded-lg border border-border p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-semibold text-hce-ink">{t('farmaciaApp.resultadoVenta.titulo', { monto: (resultado.totalCents / 100).toFixed(2) })}</p>
        <Button type="button" size="sm" variant="ghost" onClick={onCerrar}>
          {t('farmaciaApp.resultadoVenta.venderOtra')}
        </Button>
      </div>

      {resultado.vueltoTexto && <p className="text-sm text-hce-ink">{resultado.vueltoTexto}</p>}
      {resultado.avisoDispensacion && (
        <Alert variant="orange">
          <AlertDescription>{resultado.avisoDispensacion}</AlertDescription>
        </Alert>
      )}

      {estado && (
        <Alert variant={variante} role={estado.estado === 'rechazada' ? 'alert' : 'status'}>
          <AlertDescription className="space-y-1.5">
            <p className="flex flex-wrap items-center gap-x-2 gap-y-1 font-semibold text-hce-ink">
              <Icono className="h-4 w-4 shrink-0" aria-hidden="true" />
              {estado.etiqueta}
            </p>
            <p>{estado.descripcion}</p>
            {estado.motivo && <p>Motivo: {estado.motivo}</p>}
            {estado.estado === 'rechazada' && <p className="font-medium">{t('farmaciaApp.resultadoVenta.avisoRechazada')}</p>}
            {sondear && (
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Button type="button" size="sm" variant="secondary" disabled={consultando} onClick={() => void consultar()}>
                  {consultando ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />}
                  {consultando ? t('farmaciaApp.resultadoVenta.actualizando') : t('farmaciaApp.resultadoVenta.actualizar')}
                </Button>
                <span className="text-xs">{errorActualizar ? t('farmaciaApp.resultadoVenta.errorActualizar') : t('farmaciaApp.resultadoVenta.ayudaSondeo')}</span>
              </div>
            )}
          </AlertDescription>
        </Alert>
      )}

      {tieneNumero ? (
        <Button type="button" size="sm" onClick={() => void bajarComprobante()}>
          {t('farmaciaApp.resultadoVenta.descargarComprobante')}
        </Button>
      ) : (
        resultado.printableHtml && (
          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-amber-800">{t('farmaciaApp.resultadoVenta.comprobanteProvisionalTitulo')}</p>
            <p className="text-xs text-hce-muted">{t('farmaciaApp.resultadoVenta.comprobanteProvisionalAyuda')}</p>
            <iframe
              sandbox="allow-modals allow-same-origin"
              title={t('farmaciaApp.resultadoVenta.comprobanteProvisionalTitulo')}
              className="h-72 w-full rounded-xl border border-border"
              srcDoc={resultado.printableHtml}
            />
          </div>
        )
      )}
    </div>
  );
}
